/**
 * RequiredFieldsSummary — read-only display of a program's resolved Required
 * Fields settings, grouped by component (components sharing an identical
 * config collapse into one block — the common case renders as a single list).
 *
 * Props:
 *   groups — [{ component_names: string[], fields: [{ key, label, group, tier, client_alias, aliases }] }]
 *            from GET /qa-rules/program/{id}/required-fields-summary
 *
 * Read-only — QARules.js keeps its own editable version of this same pattern
 * (a different enough UI, with per-field tier selects and group bulk-apply,
 * to leave as its own implementation).
 */
const TIER_STYLE = {
  reject:   { label: 'Required',            bg: '#fdedec', color: '#c0392b' },
  review:   { label: 'Requested',            bg: '#fef9e7', color: '#b7770d' },
  optional: { label: 'Optional',             bg: '#f0f0f0', color: '#888' },
};

export default function RequiredFieldsSummary({ groups }) {
  if (!groups) return null;
  if (groups.length === 0) return null;

  return (
    <div style={{ background: '#fbfcfd', border: '1px solid #e8ecef', borderRadius: '8px', padding: '14px' }}>
      <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
        Any additional columns in your file are accepted, but only the fields below are required or tracked for this program.
        <br />
        See info bubbles for acceptable aliases. Always check with PM for program preferences.
      </div>
      {groups.map((g, i) => {
        const byGroup = [];
        g.fields.forEach(f => {
          let entry = byGroup.find(([grp]) => grp === f.group);
          if (!entry) { entry = [f.group, []]; byGroup.push(entry); }
          entry[1].push(f);
        });
        return (
          <div key={i} style={{ marginBottom: i < groups.length - 1 ? '16px' : 0 }}>
            {g.component_names?.length > 0 && groups.length > 1 && (
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a6da8', marginBottom: '6px' }}>
                Applies to: {g.component_names.join(', ')}
              </div>
            )}
            {g.fields.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>No fields tracked for this program.</div>
            ) : (
              byGroup.map(([group, groupFields]) => (
                <div key={group} style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#1a6da8', fontSize: '11px', marginBottom: '2px' }}>{group}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', columnGap: '14px' }}>
                    {groupFields.map(f => {
                      const t = TIER_STYLE[f.tier] || TIER_STYLE.optional;
                      const aliases = f.aliases || [];
                      return (
                        <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '3px 0', fontSize: '13px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '10px', background: t.bg, color: t.color, whiteSpace: 'nowrap' }}>
                            {t.label}
                          </span>
                          <span>
                            {f.label}
                            {f.client_alias && (
                              <span style={{ color: '#1a6da8', fontWeight: 600 }}> (as "{f.client_alias}")</span>
                            )}
                          </span>
                          {aliases.length > 0 && (
                            <span
                              title={`Upload files can also use these column headers for this field: ${aliases.join(', ')}`}
                              style={{ color: '#8ba7c4', fontSize: '11px', cursor: 'help' }}
                            >
                              ⓘ
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
